/* eslint-disable @typescript-eslint/naming-convention */
import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getStorageObject } from 'src/utils/storage-manager';
import { formatLanguage } from 'src/utils/translate';

import { CustomSnackbarService } from './custom-snackbar.service';

interface AIWidgetParams {
  AFO: string | null;
  MOD: string | null;
  UDI: string | null;
  HTM: string[];
  route: string | null;
  student_id: string | null;
  student: {
    email: string;
    name: string;
    remote_id: string;
    surname: string;
  } | null;
  course_remote_id: string | null;
  course_module_remote_ids: string[];
  count?: number;
  language?: string;
  analytics?: {
    measurementId: string;
  };
}

interface PostMessageData {
  type: string;
  data?: any; // Consider making this more specific if possible
  function?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AiTutorService {
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private snackbarService = inject(CustomSnackbarService);

  private widgetMounted = false;
  private scriptInjected = false;
  private moodleRoutePattern = /^\/course\/\d+\/moodle/;
  private moodleIframeSelector = "iframe[name='moodle_iframe']";

  public initialize(): void {
    this.setupMessageListener();
    this.loadScript();
    this.setupRouteListener();
  }

  private loadScript(): void {
    if (this.scriptInjected) {
      return;
    }

    const tutorAIScript = this.document.createElement('script');
    tutorAIScript.type = 'module';
    tutorAIScript.id = 'tutorAIScript';
    tutorAIScript.src = environment.aiTutorScriptUrl;
    tutorAIScript.setAttribute('data-clientId', 'bXlseHA=');
    tutorAIScript.onerror = () => {
      // eslint-disable-next-line no-console
      console.error('AI Tutor script failed to load.');
      this.snackbarService.present(this.translate.instant('Errors.AITutor.ScriptLoadError'));
      // Potentially add more robust error handling or user notification here
    };
    this.document.body.appendChild(tutorAIScript);
    this.scriptInjected = true;
  }

  private setupMessageListener(): void {
    window.addEventListener('message', (event) => {
      const data = event.data as PostMessageData;

      if (data && data.type === 'AI_INTEGRATION_LOADED' && data.data === true) {
        this.requestParams();
      }
      if (data && data.type === 'AI_PARAMS') {
        this.handleAIParams(data);
      }
    });
  }

  private handleAIParams(data: any): void {
    const val = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
    const AFOKeyInRoute =
      val.route && typeof val.route === 'string'
        ? (val.route as string).split('/').find((item) => item.toLowerCase().includes('afo'))
        : null;
    const MOD = val.MOD || null;
    const HTM = val.HTM || [];
    const AFO = val.AFO ?? AFOKeyInRoute;
    const UDI = val.UDI || null;
    const route = val.route || null;
    const student_id = val.student_id || null;
    const student = val.student || null;

    const params: AIWidgetParams = {
      course_remote_id: MOD,
      course_module_remote_ids: HTM,
      student_id,
      student,
      MOD,
      HTM,
      AFO,
      UDI,
      route,
      language: getStorageObject(environment.currentUserLanguageStorage) || formatLanguage(navigator.language),
      count: 12,
      analytics: {
        measurementId: 'GTM-PF478ZX6'
      }
    };

    const currentPath = this.router.url.split('?')[0];
    if (this.moodleRoutePattern.test(currentPath)) {
      if (!this.widgetMounted) {
        this.mountWidget(params);
      } else {
        this.updateParams(params);
      }
    }
  }

  private setupRouteListener(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const [path] = event.urlAfterRedirects.split('?');

        if (this.moodleRoutePattern.test(path)) {
          if (!this.widgetMounted) {
            this.mountWidget();
          }
        } else {
          this.unmountWidget();
        }
      });
  }

  public mountWidget(params?: AIWidgetParams): void {
    if (params?.AFO && !this.widgetMounted && (window as any).TutorWidget) {
      (window as any).TutorWidget.mount(
        'tutor-widget',
        params || {
          course_remote_id: '',
          course_module_remote_ids: [''],
          student_id: '',
          student: null,
          AFO: '',
          MOD: '',
          UDI: '',
          HTM: [''],
          count: 12,
          language: getStorageObject(environment.currentUserLanguageStorage) || formatLanguage(navigator.language),
          route: '',
          analytics: {
            measurementId: 'GTM-PF478ZX6'
          }
        }
      );
      this.widgetMounted = true;
      this.requestParams();
    }
  }

  public unmountWidget(): void {
    if (this.widgetMounted && (window as any).TutorWidget) {
      (window as any).TutorWidget.unmount();
      this.widgetMounted = false;
    }
  }

  public updateParams(params: AIWidgetParams): void {
    if (this.widgetMounted && (window as any).TutorWidget) {
      (window as any).TutorWidget.updateParams(params);
    }
  }

  private requestParams(): void {
    const moodleIframe = this.document.querySelector(this.moodleIframeSelector);
    try {
      if (moodleIframe instanceof HTMLIFrameElement && moodleIframe.contentWindow) {
        moodleIframe.contentWindow.postMessage({ function: 'get_ai_params' } as PostMessageData, '*');
      }
    } catch (e) {
      this.snackbarService.present(this.translate.instant('Errors.AITutor.RequestingParamsError'));
    }
  }
}
